import { useState, useEffect, useCallback } from 'react'
import { Webhook, Plus, Pencil, Trash2, Send } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { toast } from '../../lib/toast.js'
import getFlowActions from '../../functions/API/getFlowActions.js'
import getActionVariables from '../../functions/API/getActionVariables.js'
import postDeleteAction from '../../functions/API/postDeleteAction.js'
import postTestAction from '../../functions/API/postTestAction.js'
import ActionEditorModal from '../../modals/actionEditor.js'
import ActionSecretModal from '../../modals/actionSecret.js'
import ActionTestResultModal from '../../modals/actionTestResult.js'

/**
 * Per-step "Actions" panel for the flow editor. Self-contained: fetches its own
 * actions and the variable catalog, and owns the author/secret modals. Mounts
 * only when a step is expanded.
 */
export default function StepActionsPanel({ taskFlowId, step }) {
  const [actions, setActions] = useState([]);
  const [variables, setVariables] = useState([]);
  const [fields, setFields] = useState([]);
  const [results, setResults] = useState([]);
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [loading, setLoading] = useState(true);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [secretOpen, setSecretOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [testOpen, setTestOpen] = useState(false);
  const [testAction, setTestAction] = useState(null);

  const loadActions = useCallback(async () => {
    try {
      const res = await getFlowActions({ task_flow_id: taskFlowId, task_flow_step_id: step.id });
      if (res.response.ok) setActions(res.data.actions || []);
    } catch (e) {
      // Non-fatal — the panel simply shows no actions.
    } finally {
      setLoading(false);
    }
  }, [taskFlowId, step.id]);

  useEffect(() => {
    loadActions();
    getActionVariables({ task_flow_id: taskFlowId, task_flow_step_id: step.id }).then((res) => {
      if (res.response.ok) {
        setVariables(res.data.variables || []);
        setFields(res.data.fields || []);
        setResults(res.data.results || []);
        setDefaultTemplate(res.data.default_template || '');
      }
    }).catch(() => {});
  }, [loadActions, taskFlowId, step.id]);

  const openCreate = (e) => { e.stopPropagation(); setEditing(null); setEditorOpen(true); }
  const openEdit = (e, action) => { e.stopPropagation(); setEditing(action); setEditorOpen(true); }

  const handleShowSecret = (s) => { setSecret(s); setSecretOpen(true); }

  const handleTest = async (e, action) => {
    e.stopPropagation();
    try {
      const res = await postTestAction(action.id);
      if (res.response.ok) {
        setTestAction(action);
        setTestOpen(true);
      } else {
        toast.error('Could not queue the test.');
      }
    } catch (err) {
      toast.error('Could not queue the test.');
    }
  }

  const handleDelete = async (e, action) => {
    e.stopPropagation();
    if (!window.confirm(`Delete action "${action.name}"? This also removes its delivery history.`)) return;
    try {
      const res = await postDeleteAction(action.id);
      if (res.response.ok) { toast.success('Action deleted.'); loadActions(); }
      else toast.error('Could not delete the action.');
    } catch (err) {
      toast.error('Could not delete the action.');
    }
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">Actions</h4>

      {loading ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 py-1">Loading…</p>
      ) : (
        <div className="space-y-1">
          {actions.map((action) => (
            <div
              key={action.id}
              className="flex items-center justify-between gap-2 p-2 rounded-md border bg-sky-50 dark:bg-sky-900/30 border-sky-200 dark:border-sky-800 cursor-pointer hover:bg-sky-100 dark:hover:bg-sky-900/40"
              onClick={(e) => openEdit(e, action)}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Webhook className="h-3 w-3 text-sky-600 dark:text-sky-400 shrink-0" />
                <span className="text-xs font-medium text-sky-800 dark:text-sky-300 truncate">{action.name}</span>
                {!action.is_active && <Badge variant="secondary" className="text-[10px] py-0">Inactive</Badge>}
                <span className="text-[11px] text-sky-600/70 dark:text-sky-400/70 truncate">{action.url}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button className="p-1 rounded hover:bg-sky-200/60 dark:hover:bg-sky-800/60" title="Send test" onClick={(e) => handleTest(e, action)}>
                  <Send className="h-3 w-3 text-sky-600 dark:text-sky-400" />
                </button>
                <button className="p-1 rounded hover:bg-sky-200/60 dark:hover:bg-sky-800/60" title="Edit" onClick={(e) => openEdit(e, action)}>
                  <Pencil className="h-3 w-3 text-sky-600 dark:text-sky-400" />
                </button>
                <button className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/40" title="Delete" onClick={(e) => handleDelete(e, action)}>
                  <Trash2 className="h-3 w-3 text-red-500" />
                </button>
              </div>
            </div>
          ))}

          <Button
            size="sm"
            variant="outline"
            className="text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800 hover:bg-sky-50 dark:hover:bg-sky-900/30"
            onClick={openCreate}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Action
          </Button>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 pt-0.5">Actions fire in the background when this step completes. Customers never see them.</p>
        </div>
      )}

      <ActionEditorModal
        open={editorOpen}
        setOpen={setEditorOpen}
        taskFlowId={taskFlowId}
        step={step}
        action={editing}
        variables={variables}
        fields={fields}
        results={results}
        defaultTemplate={defaultTemplate}
        onSaved={loadActions}
        onShowSecret={handleShowSecret}
      />
      <ActionSecretModal open={secretOpen} setOpen={setSecretOpen} secret={secret} />
      <ActionTestResultModal open={testOpen} setOpen={setTestOpen} actionId={testAction?.id} actionName={testAction?.name} />
    </div>
  )
}
